import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal, Spinner } from "react-bootstrap";
import YoutubeVideoData from "../models/YoutubeVideoData";
import { getCachedYoutubeVideos } from "../utils/homePageCache";
import axiosInstance from "../services/BaseService";

interface YoutubeVideoGalleryProps {
  showSpecialOnly?: boolean;
  limit?: number;
  videos?: YoutubeVideoData[];
  useCached?: boolean;
}

const YoutubeVideoGallery: React.FC<YoutubeVideoGalleryProps> = ({ 
  showSpecialOnly = false,
  limit = 8,
  videos: providedVideos,
  useCached = false
}) => {
  const [videos, setVideos] = useState<YoutubeVideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<YoutubeVideoData | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (providedVideos) {
      setVideos(providedVideos);
      setLoading(false);
    } else {
      loadVideos();
    }
  }, [providedVideos, showSpecialOnly, limit]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      
      if (useCached) {
        // Use cached version
        const data = await getCachedYoutubeVideos(limit, showSpecialOnly);
        setVideos(data.videos || []);
      } else {
        // Use direct API call
        const response = await axiosInstance.get('/youtube-videos', {
          params: {
            limit,
            isActive: true,
            ...(showSpecialOnly && { isSpecial: true })
          }
        });
        
        const data = response.data;
        setVideos(data.videos);
        
        // Store in cache for future use
        const { cacheSet } = await import('../utils/cacheUtil');
        await cacheSet(`youtube_videos_${limit}_${showSpecialOnly}`, data, 21600); // 6 hours TTL
      }
    } catch (error) {
      console.error("Failed to load videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (video: YoutubeVideoData) => {
    setSelectedVideo(video);
    setShowModal(true);
  };

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return (
    <div className="py-4">
      <Container>
        <h2 className="mb-4">
          <i className="bi bi-youtube me-2 text-danger"></i>
          {showSpecialOnly ? "Featured Videos" : "Video Gallery"}
        </h2>
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : videos.length > 0 ? (
          <Row className="g-4">
            {videos.map((video) => {
              const videoId = getYoutubeVideoId(video.videoUrl);
              return (
                <Col key={video._id} lg={3} md={4} sm={6}>
                  <Card 
                    className="h-100 shadow-sm video-card"
                    onClick={() => handleVideoClick(video)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="position-relative">
                      <Card.Img 
                        variant="top" 
                        src={video.thumbnailUrl || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                        alt={video.title}
                        style={{ height: "180px", objectFit: "cover" }}
                      />
                      <div 
                        className="position-absolute top-50 start-50 translate-middle"
                        style={{ color: 'white', fontSize: '3rem', opacity: 0.8 }}
                      >
                        <i className="bi bi-play-circle-fill"></i>
                      </div>
                      {video.isSpecial && (
                        <div 
                          className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded-pill"
                          style={{ backgroundColor: 'rgba(255, 193, 7, 0.8)', fontSize: '0.7rem', fontWeight: 'bold' }}
                        >
                          FEATURED
                        </div>
                      )}
                    </div>
                    <Card.Body>
                      <Card.Title className="h6">{video.title}</Card.Title>
                      {video.description && (
                        <Card.Text className="small text-muted">
                          {video.description.length > 80 
                            ? `${video.description.substring(0, 80)}...` 
                            : video.description}
                        </Card.Text>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="text-center py-5">
            <i className="bi bi-film display-1 text-muted"></i>
            <h4 className="mt-3 text-muted">No videos available</h4>
          </div>
        )}
      </Container>

      {/* Video Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedVideo?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedVideo && (
            <div className="ratio ratio-16x9">
              <iframe
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(selectedVideo.videoUrl)}`}
                title={selectedVideo.title}
                allowFullScreen
              ></iframe>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100">
            {selectedVideo?.description && (
              <p className="mb-3">{selectedVideo.description}</p>
            )}
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default YoutubeVideoGallery;